//
// Copyright (c) 2011-2026 by Curalate, Inc.
//

import React, { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { SamplingCampaignRequest } from '@apis/campaign-requests/sampling/campaign-requests/samplingCampaignRequestsApiModel';
import { PrepaidIssuesTable } from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/PrepaidIssuesTable';
import {
  PrepaidContinueOption,
  PrepaidSubmissionOptions,
} from '@campaigns/components/campaign-requests/sampling-campaigns/edit/prepaid/modals/PrepaidSubmissionOptions';
import { SAMPLING_CAMPAIGNS_TRACKING_ID } from '@campaigns/constants/sampling/SamplingCampaignsTrackingId';
import {
  getLocaleDetails,
  getWhoShipsProductText,
} from '@campaigns/utils/sampling/SamplingCampaignRequestUtils';
import { PrepaidConfirmationIssues } from '@campaigns/utils/sampling/SamplingPrepaidValidationUtils';
import { Alert } from '@components/atoms/alert/Alert';
import { BodyBoldText, BodyText } from '@components/atoms/text/Text';
import { Modal } from '@components/molecules/modal/Modal';
import { isDefined } from '@utils/filterPredicates';

interface RequestPrepaidCampaignModalProps {
  closeModal: () => void;
  isModalOpen: boolean;
  isRequesting: boolean;
  isSaveError: boolean;
  issues: PrepaidConfirmationIssues;
  onCancel: () => void;
  onChangePurchaseOption: () => void;
  onRequestNewPurchase: () => void;
}

export const RequestPrepaidCampaignModal = ({
  closeModal,
  isModalOpen,
  isRequesting,
  isSaveError,
  issues,
  onCancel,
  onChangePurchaseOption,
  onRequestNewPurchase,
}: RequestPrepaidCampaignModalProps) => {
  const { getValues } = useFormContext<SamplingCampaignRequest>();
  const { fulfillmentOptions, locale } = getValues();

  const [selectedOption, setSelectedOption] = useState<
    PrepaidContinueOption | undefined
  >(undefined);

  useEffect(() => {
    if (!isModalOpen) {
      setSelectedOption(undefined);
    }
  }, [isModalOpen]);

  const handleContinue = () => {
    if (selectedOption === PrepaidContinueOption.RequestNewPurchase) {
      onRequestNewPurchase();
    } else if (selectedOption === PrepaidContinueOption.ChangePurchaseOption) {
      onChangePurchaseOption();
    }
  };

  return (
    <Modal
      closeModal={closeModal}
      contents={
        <ContentWrapper>
          {isSaveError && (
            <Alert type='error'>
              An unexpected error occurred. Please try again or contact
              Bazaarvoice Support.
            </Alert>
          )}
          <BodyText>
            It looks like some attributes in your requested campaign are outside
            the scope of your selected purchases. Please review the difference
            below and choose how you would like to proceed.
          </BodyText>
          {isDefined(locale) && isDefined(fulfillmentOptions?.whoShipsProduct) && (
            <BodyBoldText>
              {getLocaleDetails(locale)?.label} |{' '}
              {getWhoShipsProductText(fulfillmentOptions.whoShipsProduct)}
            </BodyBoldText>
          )}
          <PrepaidIssuesTable issues={issues} />
          <BodyText>Choose a Resolution</BodyText>
          <PrepaidSubmissionOptions
            onOptionChange={setSelectedOption}
            selectedOption={selectedOption}
          />
        </ContentWrapper>
      }
      isModalOpen={isModalOpen}
      primaryButtonInfo={{
        buttonContent: 'Continue',
        isDisabled: selectedOption === undefined || isRequesting,
        onClick: handleContinue,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_EDIT_PREPAID_REQUEST_MODAL_SUBMIT,
      }}
      shouldIncludeSecondaryButton={false}
      shouldIncludeTertiaryButton={true}
      tertiaryButtonInfo={{
        buttonContent: 'Cancel',
        isDisabled: isRequesting,
        onClick: onCancel,
        trackingId:
          SAMPLING_CAMPAIGNS_TRACKING_ID.CAMPAIGN_REQUEST_EDIT_PREPAID_REQUEST_MODAL_CANCEL,
      }}
      title='Campaign Request Mismatch'
      width='wide'
    />
  );
};

const ContentWrapper = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[4]};
`,
);
